import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ShoppingCart,
  CheckCircle,
  Star,
  Assignment,
  WorkOutline,
  TrendingUp,
  Architecture,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import StripeCheckoutButton from '../components/StripeCheckoutButton';

const CheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [orderBumpSelected, setOrderBumpSelected] = useState(false);
  const [systemDesignOrderBumpSelected, setSystemDesignOrderBumpSelected] = useState(false);

  const product = searchParams.get('product');

  useEffect(() => {
    document.title = 'Complete Your Order | Ray Jones';
  }, []);
  
  // Price ID mapping for Stripe
  // leetcode-roadmap: prod_SvD9M0caNlgkfo - $11
  // leetcode-roadmap-system-design: prod_SzSs6oMiUWlWAn - $18
  // leetcode-roadmap-full-bundle: prod_SzSqbijjdXdg2a - $45
  const priceIdMap: Record<string, string> = {
    'leetcode-roadmap': process.env.REACT_APP_LEETCODE_ROADMAP_PRICE_ID || 'price_1TjSSxAS9lDGoaEL39amXiJY',
    'leetcode-roadmap-bundle': process.env.REACT_APP_LEETCODE_ROADMAP_BUNDLE_PRICE_ID || 'price_1TjSSyAS9lDGoaEL7tMwa8Ci',
    'leetcode-roadmap-system-design': process.env.REACT_APP_LEETCODE_ROADMAP_SYSTEM_DESIGN_PRICE_ID || 'price_1TjSSxAS9lDGoaELzGI6p7Ps',
    'leetcode-roadmap-full-bundle': process.env.REACT_APP_LEETCODE_ROADMAP_FULL_BUNDLE_PRICE_ID || 'price_1TjSSyAS9lDGoaELUe1EgcSm',
    'swe-job-course': process.env.REACT_APP_SWE_JOB_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'swe-job-course-bundle': process.env.REACT_APP_COMPLETE_GUIDE_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'swe-job-course-system-design': process.env.REACT_APP_SWE_JOB_SYSTEM_DESIGN_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'swe-job-course-full-bundle': process.env.REACT_APP_SWE_JOB_FULL_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'behavioral-questions': process.env.REACT_APP_BEHAVIORAL_QUESTIONS_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'behavioral-questions-bundle': process.env.REACT_APP_COMPLETE_GUIDE_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'behavioral-questions-system-design': process.env.REACT_APP_BEHAVIORAL_SYSTEM_DESIGN_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'behavioral-questions-full-bundle': process.env.REACT_APP_BEHAVIORAL_FULL_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'resume-guide': process.env.REACT_APP_RESUME_GUIDE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'resume-guide-bundle': process.env.REACT_APP_COMPLETE_GUIDE_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'resume-guide-system-design': process.env.REACT_APP_RESUME_SYSTEM_DESIGN_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'resume-guide-full-bundle': process.env.REACT_APP_RESUME_FULL_BUNDLE_PRICE_ID || 'price_1RtKX0AaWcEJoDWG48687ZoB',
    'production-test': process.env.REACT_APP_PRODUCTION_TEST_PRICE_ID || 'price_1TjTP6AFlZQpjEwR2CSOmQvk',
  };

  // Product details
  const productDetails: Record<string, {
    title: string;
    description: string;
    price: number;
    originalPrice?: number;
    features: string[];
    icon: React.ReactNode;
  }> = {
    'leetcode-roadmap': {
      title: 'Complete LeetCode Roadmap',
      description: 'Systematic beginner-to-FANG coding interview preparation with 650+ problems',
      price: 11,
      features: [
        'Complete 90-day curriculum',
        'Beginner Roadmap (50+ Problems)',
        'Fortune 500 Interview Roadmap (70+ Problems)', 
        'FANG Interview Roadmap (400+ Problems)',
        'My Entire Top 2.5% Leetcode Journey (700+ problems)',
      ],
      icon: <Assignment />
    },
    'swe-job-course': {
      title: '90-Day SWE Job Landing Course',
      description: 'Complete roadmap to land your software engineering job',
      price: 11,
      features: [
        'Guide to Coding Interviews (DSA / Take Home projects / etc.)',
        'Resume optimization templates',
        'Interview preparation materials'
      ],
      icon: <WorkOutline />
    },
    'behavioral-questions': {
      title: '10 Behavioral Questions You\'ll Be Asked',
      description: 'Master every behavioral interview with proven frameworks',
      price: 11,
      features: [
        '10 most common behavioral questions',
        'STAR method frameworks',
        'Sample answers and templates',
        'Interview tips and strategies'
      ],
      icon: <Star />
    },
    'resume-guide': {
      title: 'Resume & LinkedIn Optimization Bundle',
      description: 'Get the exact resume and LinkedIn strategy that lands interviews',
      price: 11,
      features: [
        'My Exact College and Mid-Level SWE Resume',
        'LinkedIn optimization guide',
        'ATS-friendly formatting',
        'Industry-specific tips'
      ],
      icon: <TrendingUp />
    },
    'production-test': {
      title: 'Production Checkout Test',
      description: 'Small live payment to verify production checkout, webhooks, and purchase automation.',
      price: 0.50,
      features: [
        'Live Stripe Checkout test',
        'Webhook delivery verification',
        'Safe to refund after confirmation'
      ],
      icon: <ShoppingCart />
    }
  };

  // Order bump details
  const orderBump = {
    title: 'Raymond\'s Complete Guide to SWE',
    description: 'The guide that landed me a $195k/year job offer',
    price: 27,
    originalPrice: 33,
    features: [
      'My actual resume that got me hired',
      'LinkedIn optimization guide',
      'Beginner to Advanced Leetcode Roadmap',
      '~2 hour Video Breakdown',
    ]
  };

  // Second order bump details
  const systemDesignOrderBump = {
    title: 'System Design Roadmap',
    description: 'Master LLD and mid-level system design interviews',
    price: 7,
    originalPrice: 11,
    features: [
      'Complete LLD (Low Level Design) preparation',
      'Mid-level system design interview guide',
      'Real interview scenarios and solutions',
      'Architecture patterns and best practices',
    ]
  };

  const selectedProduct = productDetails[product || 'leetcode-roadmap'];
  if (!selectedProduct) {
    return null;
  }

  const isProductionTestProduct = product === 'production-test';
  const formatPrice = (price: number) => `$${price.toFixed(2)}`;
  const basePrice = selectedProduct.price;
  const totalPrice = basePrice + (!isProductionTestProduct && orderBumpSelected ? orderBump.price : 0) + (!isProductionTestProduct && systemDesignOrderBumpSelected ? systemDesignOrderBump.price : 0);

  const getSelectedPriceId = () => {
    const baseProductKey = product || 'leetcode-roadmap';

    if (baseProductKey === 'production-test') {
      return priceIdMap[baseProductKey];
    }
    
    if (orderBumpSelected && systemDesignOrderBumpSelected) {
      // Both order bumps selected - use full bundle
      return priceIdMap[`${baseProductKey}-full-bundle`] || priceIdMap[baseProductKey];
    } else if (orderBumpSelected) {
      // Only first order bump selected - use regular bundle
      return priceIdMap[`${baseProductKey}-bundle`] || priceIdMap[baseProductKey];
    } else if (systemDesignOrderBumpSelected) {
      // Only system design order bump selected
      return priceIdMap[`${baseProductKey}-system-design`] || priceIdMap[baseProductKey];
    }
    // No order bumps selected - use regular product price ID
    return priceIdMap[baseProductKey];
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Side - Product Details */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ShoppingCart sx={{ color: 'secondary.main', fontSize: 32, mr: 2 }} />
                  <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    Order Summary
                  </Typography>
                </Box>
                
                <Card sx={{ 
                  bgcolor: 'background.default',
                  border: '2px solid #333',
                  mb: 4
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2, color: 'secondary.main' }}>
                        {selectedProduct.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                          {selectedProduct.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
                          {selectedProduct.description}
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                        {formatPrice(selectedProduct.price)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2, bgcolor: '#333' }} />
                    
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                      What's Included:
                    </Typography>
                    {selectedProduct.features.map((feature, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle sx={{ color: 'secondary.main', fontSize: 20, mr: 2 }} />
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>

                {!isProductionTestProduct && (
                <>
                {/* Order Bump Section */}
                <Card sx={{
                  bgcolor: 'rgba(136, 192, 208, 0.1)',
                  border: orderBumpSelected ? '3px solid #88C0D0' : '2px solid #88C0D0',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible'
                }}>
                  {/* Dotted line and arrow pointing to upgrade text */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -40,
                      top: 60,
                      width: 35,
                      height: 2,
                      borderTop: '2px dotted #88C0D0',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: -5,
                        top: -3,
                        width: 0,
                        height: 0,
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                        borderLeft: '8px solid #88C0D0'
                      }
                    }}
                  />

                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={orderBumpSelected}
                            onChange={(e) => setOrderBumpSelected(e.target.checked)}
                            sx={{
                              color: 'secondary.main',
                              '&.Mui-checked': { color: 'secondary.main' }
                            }}
                          />
                        }
                        label=""
                        sx={{ mr: 2 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: orderBumpSelected ? '#88C0D0' : 'text.primary', 
                              fontWeight: 'bold',
                              background: orderBumpSelected ? 'rgba(136, 192, 208, 0.2)' : 'transparent',
                              padding: '8px 12px',
                              borderRadius: 2,
                              border: orderBumpSelected ? '2px solid #88C0D0' : 'none',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            ✅ Yes! Upgrade my order with: {orderBump.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <Chip 
                              label="Upgrade" 
                              sx={{ 
                                bgcolor: 'secondary.main', 
                                color: 'black', 
                                fontWeight: 'bold',
                                animation: orderBumpSelected ? 'none' : 'pulse 2s infinite'
                              }} 
                            />
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                          {orderBump.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                            ${orderBump.price}
                          </Typography>
                        </Box>

                        {orderBump.features.map((feature, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: 'secondary.main', fontSize: 16, mr: 1 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Second Order Bump Section - System Design Roadmap */}
                <Card sx={{ 
                  bgcolor: 'rgba(255, 193, 7, 0.1)',
                  border: systemDesignOrderBumpSelected ? '3px solid #FFD700' : '2px solid #FFD700',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible',
                  mt: 3
                }}>
                  {/* Dotted line and arrow pointing to upgrade text */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: -40,
                      top: 60,
                      width: 35,
                      height: 2,
                      borderTop: '2px dotted #FFD700',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: -5,
                        top: -3,
                        width: 0,
                        height: 0,
                        borderTop: '4px solid transparent',
                        borderBottom: '4px solid transparent',
                        borderLeft: '8px solid #FFD700'
                      }
                    }}
                  />

                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={systemDesignOrderBumpSelected}
                            onChange={(e) => setSystemDesignOrderBumpSelected(e.target.checked)}
                            sx={{
                              color: 'secondary.main',
                              '&.Mui-checked': { color: 'secondary.main' }
                            }}
                          />
                        }
                        label=""
                        sx={{ mr: 2 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: systemDesignOrderBumpSelected ? '#FFD700' : 'text.primary', 
                              fontWeight: 'bold',
                              background: systemDesignOrderBumpSelected ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
                              padding: '8px 12px',
                              borderRadius: 2,
                              border: systemDesignOrderBumpSelected ? '2px solid #FFD700' : 'none',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            ✅ Yes! Add {systemDesignOrderBump.title} for only ${systemDesignOrderBump.price}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <Architecture sx={{ color: 'secondary.main', fontSize: 24, mr: 1 }} />
                            <Chip 
                              label="Special Offer" 
                              sx={{ 
                                bgcolor: 'secondary.main', 
                                color: 'black', 
                                fontWeight: 'bold',
                                animation: systemDesignOrderBumpSelected ? 'none' : 'pulse 2s infinite'
                              }} 
                            />
                          </Box>
                        </Box>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                          {systemDesignOrderBump.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                            ${systemDesignOrderBump.price}
                          </Typography>
                        </Box>

                        {systemDesignOrderBump.features.map((feature, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: 'secondary.main', fontSize: 16, mr: 1 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                </>
                )}
              </Paper>
            </Grid>

            {/* Right Side - Order Total & Continue */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper sx={{ 
                p: 4, 
                bgcolor: 'background.paper', 
                borderRadius: 4,
                position: 'sticky',
                top: 100
              }}>
                <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 3 }}>
                  Order Total
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {selectedProduct.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {formatPrice(basePrice)}
                    </Typography>
                  </Box>
                  
                  {!isProductionTestProduct && orderBumpSelected && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {orderBump.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        ${orderBump.price}
                      </Typography>
                    </Box>
                  )}
                  
                  {!isProductionTestProduct && systemDesignOrderBumpSelected && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {systemDesignOrderBump.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        ${systemDesignOrderBump.price}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ mb: 3, bgcolor: '#333' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                    {formatPrice(totalPrice)}
                  </Typography>
                </Box>

                <StripeCheckoutButton
                  priceId={getSelectedPriceId()}
                  buttonText="Continue to Payment"
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{
                    color: 'black',
                    py: 2,
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    '&:hover': { transform: 'scale(1.02)' },
                    transition: 'transform 0.2s',
                    mb: 3,
                    width: '100%'
                  }}
                  successUrl={`/success?product=${product}`}
                  cancelUrl={`/checkout?product=${product}`}
                />

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    🔒 Secure checkout powered by Stripe
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    30-day money-back guarantee
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </Box>
  );
};

export default CheckoutPage;
